import { useEffect } from 'react';
import { ChatMachineContext } from './ChatMachineContext';
import { AIContext, AIContextEntry } from '@/apiTypes/AiTypes';

export const aiValue = <T>(value: T, description: string, anonymousToken?: string): AIContextEntry<T> => {
    return { value, description, ...(anonymousToken && { anonymousToken }) };
};

export interface UseAIContextOptions {
    persist?: boolean;
}

export const useAIContext = (context: AIContext, options: UseAIContextOptions = {}) => {
    const { persist = false } = options;
    const actorRef = ChatMachineContext.useActorRef();

    // Normalize undefined values to null for proper JSON serialization
    // This ensures keys are always tracked even when values are initially undefined
    const normalizedContext: AIContext = {};
    for (const [key, entry] of Object.entries(context)) {
        normalizedContext[key] = {
            value: entry.value ?? null,
            description: entry.description,
            ...(entry.anonymousToken && { anonymousToken: entry.anonymousToken }),
        };
    }

    // Stringify here is to help out the useEffect dependencies checker
    // since normalizedContext is fresh on every render and without
    // stringifying it would run the effect every time
    const contextJson = JSON.stringify(normalizedContext);

    useEffect(() => {
        const parsedContext = JSON.parse(contextJson) as AIContext;
        const keys = Object.keys(parsedContext);

        // Merge the context into the machine
        actorRef.send({ type: 'MERGE_AI_CONTEXT', context: parsedContext, persist });

        // Cleanup: remove these keys when component unmounts or context changes
        // Only remove if not persisted
        return () => {
            if (!persist) {
                actorRef.send({ type: 'REMOVE_AI_CONTEXT_KEYS', keys });
            }
        };
    }, [contextJson, actorRef, persist]);
};
